import { memo, useCallback } from 'react';
import isEqual from 'react-fast-compare';

import { type PublicBoard } from '@air/api/types';

import { KanbanBoardComponent } from '~/components/KanbanView/KanbanColumnsManagers/shared/KanbanColumnItems/KanbanItem/shared/KanbanBoardComponent';
import { type KanbanBoardProps } from '~/components/KanbanView/KanbanColumnsManagers/shared/KanbanColumnItems/KanbanItem/shared/kanbanItemTypes';
import { useGetPublicBoardMenuOptionsCallback } from '~/components/PublicBoard/useGetPublicBoardMenuOptionsCallback';
import { PublicGalleryBoardAncestors } from '~/components/PublicGallery/PublicGalleryBoardAncestors';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { usePublicSelectionMenuOptions } from '~/hooks/usePublicSelectionMenuOptions';
import { usePublicBoard } from '~/providers/PublicBoardProvider';
import { isMobileAgent } from '~/utils/PlatformHelpers';
import { canChangeBoardCustomFields, canSeeBoardCustomFields } from '~/utils/permissions/boardPermissions';
import { canGenerateZip } from '~/utils/permissions/taskPermissions';

export const PublicKanbanBoard = memo(({ board, kanbanDndItemData, style }: KanbanBoardProps<PublicBoard>) => {
  const { isSearchActive } = useIsSearchActive();
  const { permissions } = usePublicPermissions();
  const { getSelectionMenuOptions } = usePublicSelectionMenuOptions();
  const { getBoardMenuOptionsCallback } = useGetPublicBoardMenuOptionsCallback();
  const _getItemMenuOptions = getBoardMenuOptionsCallback({ item: board });
  const { onNavigateToBoard } = usePublicBoard();
  const onItemClick = useCallback(
    () => onNavigateToBoard({ boardId: board.id, boardTitle: board.title }),
    [board, onNavigateToBoard],
  );

  const canDownload = canGenerateZip(permissions);
  const canEditCustomFields = canChangeBoardCustomFields(permissions);
  const canViewCustomFields = canSeeBoardCustomFields(permissions);

  const isSelectable = canDownload || canEditCustomFields;

  return (
    <KanbanBoardComponent
      board={board}
      dndDisabled={isMobileAgent || !canEditCustomFields}
      getItemMenuOptions={_getItemMenuOptions}
      getSelectionMenuOptions={getSelectionMenuOptions}
      kanbanDndItemData={kanbanDndItemData}
      onClick={onItemClick}
      isSelectable={isSelectable}
      ancestors={
        isSearchActive && !!board.ancestors?.length ? (
          <PublicGalleryBoardAncestors ancestors={board.ancestors} />
        ) : undefined
      }
      style={style}
      canViewCustomFields={canViewCustomFields}
    />
  );
}, isEqual);

PublicKanbanBoard.displayName = 'PublicKanbanBoard';
